import {
  Button,
  ColumnChart,
  DateField,
  DonutChart,
  HookedForm,
} from '@app/components';
import EmailIcon from '@app/assets/icons/email.svg?react';
import IphoneIcon from '@app/assets/icons/iphone.svg?react';
import { useState, useRef } from 'react';
import {
  getCampaignsByTimeline,
  getCampaignsInformation,
  getEmailMetrics,
  getSmsMetrics,
} from '@app/services/analytics';
import html2pdf from 'html2pdf.js';
import { formatDateToISO } from '@app/utils/utils';

interface AnalyticsData {
  campaigns: any;
  timeline: any;
}

export const AnalyticsCampaigns = () => {
  const [data, setData] = useState<AnalyticsData | undefined>();
  const [selectedPeriod, setSelectedPeriod] = useState<{ start?: string; end?: string }>({});
  const [emailMetrics, setEmailMetrics] = useState<any | undefined>();
  const [channel] = useState<'email' | 'sms'>('email');
  const [smsMetrics, setSmsMetrics] = useState<any | undefined>();
  const dashboardRef = useRef<HTMLDivElement>(null);

  const fetchData = (start?: string, end?: string, selectedChannel: 'email' | 'sms' = channel) => {
    if (start && end) {
      const promises = [
        getCampaignsInformation({ startDate: start, endDate: end }),
        getCampaignsByTimeline({ startDate: start, endDate: end }),
        selectedChannel === 'email'
          ? getEmailMetrics({ startDate: start, endDate: end })
          : getSmsMetrics({ startDate: start, endDate: end })
      ];
      Promise.all(promises)
        .then(([campaigns, timeline, metricsRes]) => {
          setData({ campaigns: campaigns.data, timeline: timeline.data });
          if (selectedChannel === 'email') {
            setEmailMetrics(metricsRes.data);
            setSmsMetrics(undefined);
          } else {
            setSmsMetrics(metricsRes.data);
            setEmailMetrics(undefined);
          }
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
        });
    }
  };

  const handleDateChange = (values: { start?: string; end?: string }) => {
    setSelectedPeriod(values);
    fetchData(values.start, values.end, channel);
  };


  // Email dashboard mapping
  const reviewed = emailMetrics?.emails_sent || 0;
  const opened = emailMetrics?.engagement_metrics?.unique_opens || 0;
  const clicked = emailMetrics?.engagement_metrics?.unique_clicks || 0;
  const unsubscribed = 1; // minimal value for visual clarity
  const delivered = emailMetrics?.emails_delivered || 0;
  const total = reviewed + opened + clicked + unsubscribed + delivered;
  const percent = (value: number) => total ? Math.round((value / total) * 100) : 0;

  // SMS dashboard mapping
  const smsReviewed = smsMetrics?.total_messages || 0;
  const smsDelivered = smsMetrics?.delivered || 0;
  const smsFailed = smsMetrics?.failed || 0;
  const smsTotal = smsReviewed + smsDelivered + smsFailed;
  const smsPercent = (value: number) => smsTotal ? Math.round((value / smsTotal) * 100) : 0;

  // Calculate scheduled follow-ups for email and sms
  const scheduledEmail = data?.campaigns?.byType?.[0]?.email?.scheduled || 0;
  const scheduledSms = data?.campaigns?.byType?.[1]?.sms?.scheduled || 0;
  const totalScheduled = scheduledEmail + scheduledSms;

  const handleExportPDF = () => {
    if (!dashboardRef.current) return;

    const element = dashboardRef.current;
    const opt = {
      margin: 1,
      filename: `analytics-dashboard-${formatDateToISO(new Date())}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    } as const;

    // Clone the element to modify it for PDF
    const clone = element.cloneNode(true) as HTMLElement;
    
    // Remove interactive elements and adjust styles for PDF
    const buttons = clone.querySelectorAll('button, select');
    buttons.forEach(button => button.remove());
    
    // Add PDF-specific class to timeline section
    const timelineSection = clone.querySelector('.timeline-section');
    if (timelineSection) timelineSection.classList.add('pdf-timeline-small', 'pdf-timeline-container');

    // Add some PDF-specific styling
    const style = document.createElement('style');
    style.textContent = `
      .pdf-content { padding: 20px 20px 4px 20px; }
      .pdf-content * { color: black !important; }
      .pdf-content canvas { max-width: 100% !important; }
      .pdf-timeline-small .ColumnChart, .pdf-timeline-small canvas {
        width: 300px !important;
        height: 160px !important;
      }
      .pdf-timeline-container {
        max-width: 900px;
        margin-left: 0;
        transform: scale(0.45);
        transform-origin: top left;
        page-break-inside: avoid;
        break-inside: avoid;
      }
    `;
    clone.appendChild(style);
    clone.classList.add('pdf-content');

    html2pdf().set(opt).from(clone).save();
  };

  return (
    <>
      <div className="bg-white border-b border-b-base-ghostWhite ">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h2 className="font-bold text-[28px]">Campaigns Analytics</h2>
          <div className="flex gap-2">
            <Button 
              className="text-sm font-semibold" 
              kind="secondary"
              onClick={handleExportPDF}
            >
              Export as PDF
            </Button>
          </div>
        </div>
        <div className="container mx-auto p-4">
          <HookedForm 
            className="flex gap-4"
            onChange={handleDateChange}
          >
            <DateField name="start" containerClass="w-36" />
            <DateField name="end" containerClass="w-36 " />
   
          </HookedForm>
        </div>
      </div>
      {!selectedPeriod.start || !selectedPeriod.end ? (
        <div className="container mx-auto p-8">
          <div className="bg-white p-8 rounded-lg text-center">
            <h3 className="text-xl font-semibold mb-2">Select a Period</h3>
            <p className="text-base-davyGray">
              Please select a start and end date to view the campaigns analytics dashboard.
            </p>
          </div>
        </div>
      ) : (
        <div ref={dashboardRef} className="container mx-auto p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 ">
              <div className="flex items-center justify-between">
                <h5 className="font-bold text-sm">Follow-ups</h5>
      
              </div>
              <hr className="my-4" />
              <div className="space-y-4 flex gap-10 items-center justify-center">
                <div>
                  <h4 className="text-6xl font-bold">{totalScheduled}</h4>
                  <p className="text-base-davyGray text-sm mt-4">Active follow ups</p>
                </div>
                <div>
                  <ul className="flex gap-2 flex-col">
                    <li className="text-base-davyGray text-sm flex items-center gap-2">
                      <div className="h-6 w-6 bg-base-antiFlash flex items-center justify-center">
                        <EmailIcon />
                      </div>
                      <span>Email</span>
                      <div className="rounded-full w-32 bg-brand-lightBlue3 h-2 overflow-hidden ">
                        <div className="bg-brand-baseBlue" style={{ width: `${scheduledEmail + scheduledSms > 0 ? (scheduledEmail / (scheduledEmail + scheduledSms)) * 100 : 0}%`, height: '100%', borderRadius: '9999px' }} />
                      </div>
                      <span>{scheduledEmail}</span>
                    </li>
                    <li className="text-base-davyGray text-sm flex items-center gap-2">
                      <div className="h-6 w-6 bg-base-antiFlash flex items-center justify-center">
                        <IphoneIcon />
                      </div>
                      <span>SMS</span>
                      <div className="rounded-full w-32 bg-brand-lightBlue3 h-2 overflow-hidden">
                        <div className="bg-brand-baseBlue" style={{ width: `${scheduledEmail + scheduledSms > 0 ? (scheduledSms / (scheduledEmail + scheduledSms)) * 100 : 0}%`, height: '100%', borderRadius: '9999px' }} />
                      </div>
                      <span>{scheduledSms}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 h-fit">
              {/* <div className="flex items-center justify-between">
                <h5 className="font-bold text-sm">
                  {channel === 'email' ? 'Sent emails' : 'Sent SMS'}
                </h5>
                <select
                  className="bg-base-antiFlash p-2 rounded-lg w-32"
                  value={channel}
                  onChange={e => handleChannelChange(e.target.value as 'email' | 'sms')}
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div> */}
              <hr className="my-4" />
              {channel === 'email' ? (
                <div className="flex items-center gap-8">
                  <DonutChart
                    data={[
                      ['Status', 'Count'],
                      ['Sent', reviewed],
                      ['Opened', opened],
                      ['Clicked', clicked],
                      ['Unsubscribed', unsubscribed],
                      ['Delivered', delivered],
                    ]}
                    width={350}
                    height={350}
                    opt={{
                      legend: 'none',
                      colors: ['#4CAF50', '#1976D2', '#42A5F5', '#EF5350', '#E0E0E0'],
                    }}
                  />
                  <div>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#4CAF50' }}></span>
                        Sent
                        <span className="ml-auto font-semibold">{reviewed.toLocaleString()}</span>
                        <span className="text-base-davyGray">({percent(reviewed)}%)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#1976D2' }}></span>
                        Opened
                        <span className="ml-auto font-semibold">{opened.toLocaleString()}</span>
                        <span className="text-base-davyGray">({percent(opened)}%)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#42A5F5' }}></span>
                        Clicked
                        <span className="ml-auto font-semibold">{clicked.toLocaleString()}</span>
                        <span className="text-base-davyGray">({percent(clicked)}%)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#EF5350' }}></span>
                        Unsubscribed
                        <span className="ml-auto font-semibold">{unsubscribed.toLocaleString()}</span>
                        <span className="text-base-davyGray">({percent(unsubscribed)}%)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#E0E0E0' }}></span>
                        Delivered
                        <span className="ml-auto font-semibold">{delivered.toLocaleString()}</span>
                        <span className="text-base-davyGray">({percent(delivered)}%)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-8">
                  <DonutChart
                    data={[
                      ['Status', 'Count'],
                      ['Reviewed', smsReviewed],
                      ['Delivered', smsDelivered],
                      ['Failed', smsFailed],
                    ]}
                    width={350}
                    height={350}
                    opt={{
                      legend: 'none',
                      colors: ['#4CAF50', '#E0E0E0', '#EF5350'],
                    }}
                  />
                  <div>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#4CAF50' }}></span>
                        Reviewed
                        <span className="ml-auto font-semibold">{smsReviewed.toLocaleString()}</span>
                        <span className="text-base-davyGray">({smsPercent(smsReviewed)}%)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#E0E0E0' }}></span>
                        Delivered
                        <span className="ml-auto font-semibold">{smsDelivered.toLocaleString()}</span>
                        <span className="text-base-davyGray">({smsPercent(smsDelivered)}%)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#EF5350' }}></span>
                        Failed
                        <span className="ml-auto font-semibold">{smsFailed.toLocaleString()}</span>
                        <span className="text-base-davyGray">({smsPercent(smsFailed)}%)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="bg-white p-4 h-fit timeline-section">
            <hr className="my-4" />
            <div className="space-y-8">
              <div>
                <div className="font-semibold text-center mb-2">Emails</div>
                <ColumnChart
                  hideLegend
                  data={[
                    ['Month', 'Emails'],
                    ...(data?.timeline?.map((item: any) => {
                      const [year, month] = (item.month || '').split('-');
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      const label = monthNames[parseInt(month, 10) - 1] + ' ' + year;
                      return [label, item.email || 0];
                    }) || []),
                  ]}
                />
              </div>
              <div>
                <div className="font-semibold text-center mb-2">Sms</div>
                <ColumnChart
                  hideLegend
                  data={[
                    ['Month', 'Sms'],
                    ...(data?.timeline?.map((item: any) => {
                      const [year, month] = (item.month || '').split('-');
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      const label = monthNames[parseInt(month, 10) - 1] + ' ' + year;
                      return [label, item.sms || 0];
                    }) || []),
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
